/**
 * Resmi (GİB şablonu) fatura penceresi: görüntüle · yazdır · PDF indir.
 * Taslak, onaylı, giden ve Kolaysoft arşivi faturaları aynı düzende açılır.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import GibInvoiceDocument from './GibInvoiceDocument';
import { buildGibQrPayload } from '../../../utils/officialInvoiceModel';

const fileNameOf = (model) => {
  const base = model?.meta?.number || `TASLAK-${String(model?.buyer?.title || 'fatura').replace(/\s+/g, '_')}`;
  return `${base}.pdf`.replace(/[\\/:*?"<>|]/g, '');
};

const printHtml = (html, title) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
<style>@page{size:A4;margin:0}html,body{margin:0;padding:0;background:#fff}
*{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body>${html}</body></html>`);
  doc.close();
  const run = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => iframe.remove(), 1500);
  };
  const imgs = Array.from(doc.images || []);
  if (!imgs.length) {
    setTimeout(run, 50);
    return;
  }
  Promise.all(imgs.map((img) => (img.complete ? null : new Promise((r) => { img.onload = r; img.onerror = r; })))).then(
    () => setTimeout(run, 50)
  );
};

/** actions: yazdır/indir'in soluna eklenecek düğmeler (ör. gelen faturada Kabul / Reddet). */
export default function OfficialInvoiceDialog({ open, onClose, model, loading = false, error = '', autoPrint = false, actions = null }) {
  const pageRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const printedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const payload = model ? buildGibQrPayload(model) : '';
    if (!payload) {
      setQrDataUrl('');
      return undefined;
    }
    QRCode.toDataURL(payload, { errorCorrectionLevel: 'M', margin: 0, width: 300 })
      .then((url) => !cancelled && setQrDataUrl(url))
      .catch(() => !cancelled && setQrDataUrl(''));
    return () => {
      cancelled = true;
    };
  }, [model]);

  useEffect(() => {
    if (!open) printedRef.current = false;
  }, [open]);

  const handlePrint = () => {
    if (!pageRef.current) return;
    printHtml(pageRef.current.innerHTML, model?.meta?.number || 'Fatura');
  };

  useEffect(() => {
    if (!open || !autoPrint || !model || loading || printedRef.current) return undefined;
    const needsQr = Boolean(buildGibQrPayload(model));
    if (needsQr && !qrDataUrl) return undefined;
    const t = setTimeout(() => {
      printedRef.current = true;
      handlePrint();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoPrint, model, loading, qrDataUrl]);

  const handleDownload = async () => {
    const node = pageRef.current?.firstElementChild;
    if (!node) return;
    setBusy(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const w = 210;
      const h = (canvas.height * w) / canvas.width;
      const img = canvas.toDataURL('image/jpeg', 0.95);
      // Uzun faturalar birden fazla sayfaya bölünür
      let offset = 0;
      pdf.addImage(img, 'JPEG', 0, 0, w, h);
      offset += 297;
      while (offset < h - 1) {
        pdf.addPage();
        pdf.addImage(img, 'JPEG', 0, -offset, w, h);
        offset += 297;
      }
      pdf.save(fileNameOf(model));
    } catch (e) {
      toast.error(`PDF oluşturulamadı: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { maxWidth: 880, width: '100%' } }}>
      <DialogContent dividers sx={{ bgcolor: '#e5e7eb', p: { xs: 1, sm: 2 } }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : model ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Box ref={pageRef} sx={{ width: 794, mx: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}>
              <GibInvoiceDocument model={model} qrDataUrl={qrDataUrl} />
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25, flexWrap: 'wrap', gap: 0.5 }}>
        {actions ? <Box sx={{ display: 'flex', gap: 1, mr: 'auto', flexWrap: 'wrap' }}>{actions}</Box> : null}
        <Button startIcon={<PrintIcon />} variant="outlined" onClick={handlePrint} disabled={!model || loading}>
          Yazdır
        </Button>
        <Button
          startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
          variant="outlined"
          onClick={handleDownload}
          disabled={!model || loading || busy}
        >
          PDF İndir
        </Button>
        <Button startIcon={<CloseIcon />} variant="contained" onClick={onClose}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
}
